// Spracheingabe (Diktat) — zwei Engines, NUR fuer Beschreibungsfelder (features.md: Voice fuellt,
// der Mensch bestaetigt; nie fuer Identifikatoren; nie ein SAP-Write):
//
//   LOKAL (bevorzugt): unternehmenseigener Sprach-Server (Whisper, OpenAI-kompatibel) — Audio
//     bleibt im Haus. Aktiv NUR wenn der Verbindungstest bestanden wurde
//     (me.features.voiceServer.verified; PUT /api/pm/admin/voice-server). Aufnahme per
//     MediaRecorder (Push-to-talk), Transkription ueber den Backend-Proxy
//     POST /api/pm/voice/transcribe. Ist der Server zur Laufzeit weg -> Meldung (serverError).
//   BROWSER (Fallback): Web Speech API — sendet Audio an den Browser-Hersteller
//     (Google/Microsoft/Apple), darum nur hinter dem unternehmensweiten Flag
//     me.features.voice.
//
// Gesperrt-Zustaende: beides aus -> Richtlinien-Meldung (Hover/Klick); Browser-Engine ohne
// SpeechRecognition-Support (Firefox) -> eigener Hinweis. Fail-closed: ohne features-Objekt aus
// (neutraler Lade-Hinweis, bis /me antwortet). SaaS ohne externe KI: /me liefert
// features.externalAI === false (voice/verified dann false) -> eigener Hinweis.
import React, { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { transcribeAudio } from '../api';

const Recognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : undefined;

/**
 * @param {Object} user — Auth fuer den Transcribe-Proxy (lokale Engine)
 * @param {string} role — aktive PM-Rolle (X-PM-Role)
 * @param {Object} features — me.features: { voice, voiceServer: {configured, verified} }
 * @param {Function} onText — (finalTranscript: string) => void
 */
function VoiceInput({ user, role, features, onText }) {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [interim, setInterim] = useState('');
  const [note, setNote] = useState('');
  const recRef = useRef(null);      // SpeechRecognition (Browser-Engine)
  const mediaRef = useRef(null);    // MediaRecorder (lokale Engine)

  const localVerified = Boolean(features && features.voiceServer && features.voiceServer.verified);
  const browserAllowed = Boolean(features && features.voice);
  const browserSupported = Boolean(Recognition);
  const mode = (!features || features.loading) ? 'loading' : features.externalAI === false ? 'aiOff' : localVerified ? 'local' : (browserAllowed ? (browserSupported ? 'browser' : 'unsupported') : 'locked');
  const locked = mode !== 'local' && mode !== 'browser';
  const lockMsg = {
    loading: t('pm.voice.loading'),
    aiOff: t('pm.voice.externalAiOff'),
    locked: t('pm.voice.disabledMsg'),
  }[mode] || t('pm.voice.unsupported');

  // Beim Unmount laufende Aufnahme/Erkennung stoppen.
  useEffect(() => () => {
    try { recRef.current && recRef.current.abort(); } catch { /* egal */ }
    try { mediaRef.current && mediaRef.current.state === 'recording' && mediaRef.current.stop(); } catch { /* egal */ }
  }, []);

  // --- Browser-Engine (Web Speech API) ---
  const startBrowser = () => {
    const rec = new Recognition();
    recRef.current = rec;
    rec.lang = language === 'en' ? 'en-US' : 'de-DE';
    rec.interimResults = true;
    rec.continuous = false; // Push-to-talk: eine Aeusserung, dann Ende
    rec.onresult = (e) => {
      let interimText = '';
      let finalText = '';
      for (const result of e.results) {
        if (result.isFinal) finalText += result[0].transcript;
        else interimText += result[0].transcript;
      }
      setInterim(interimText);
      if (finalText) onText(finalText.trim());
    };
    rec.onend = () => { setListening(false); setInterim(''); };
    rec.onerror = () => { setListening(false); setInterim(''); };
    setListening(true);
    setNote('');
    rec.start();
  };

  // --- Lokale Engine (MediaRecorder -> Backend-Proxy -> Whisper) ---
  const startLocal = async () => {
    // Kein mediaDevices-API = unsicherer Kontext (nur localhost/HTTPS exponieren das Mikro)
    // oder sehr alter Browser — praeziser Hinweis statt faelschlich "verweigert".
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setNote(t('pm.voice.insecure'));
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      // Fehlerart unterscheiden — "sofort abgelehnt ohne Prompt" hat verschiedene Ursachen:
      //   NotFoundError: kein Mikrofon-Geraet (oder Windows-Datenschutz blockt den Mikrofonzugriff
      //   fuer Apps systemweit) — es gibt nichts zu erlauben, darum KEIN Browser-Prompt.
      //   NotAllowedError/SecurityError: verweigert (Nutzer frueher / Enterprise-Policy).
      const name = (e && e.name) || 'UnknownError';
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
        setNote(`${t('pm.voice.noMic')} (${name})`);
      } else if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
        setNote(`${t('pm.voice.micDenied')} (${name})`);
      } else {
        setNote(`${t('pm.voice.micError')} (${name})`);
      }
      return;
    }
    // Bevorzugtes Format je Browser (Chrome/Edge: webm, Safari: mp4, Firefox: ogg via Default).
    const mimeType = ['audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    mediaRef.current = recorder;
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = async () => {
      stream.getTracks().forEach((tr) => tr.stop());
      setListening(false);
      // Tatsaechlichen Aufnahme-Typ verwenden (recorder.mimeType kann Codec-Suffixe tragen) —
      // ehrliches Content-Type-Label fuer den Proxy/Whisper statt hartem webm-Fallback.
      const actualType = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0];
      const blob = new Blob(chunks, { type: actualType });
      if (blob.size === 0) return;
      setTranscribing(true);
      try {
        const res = await transcribeAudio(user, blob, language === 'en' ? 'en' : 'de', role);
        if (res && res.text) onText(res.text);
      } catch {
        // Server konfiguriert, aber gerade nicht erreichbar/Modell weg -> Meldung statt Stille.
        setNote(t('pm.voice.serverError'));
      } finally {
        setTranscribing(false);
      }
    };
    setListening(true);
    setNote('');
    recorder.start();
  };

  const stop = () => {
    if (mode === 'local') { try { mediaRef.current && mediaRef.current.stop(); } catch { /* egal */ } }
    else { try { recRef.current && recRef.current.stop(); } catch { /* egal */ } }
  };

  const onClick = () => {
    if (locked) {
      // Klick auf gesperrtes Mikro: Richtlinien-/Support-Hinweis einblenden (Hover via title).
      setNote(lockMsg);
      return;
    }
    if (listening) stop();
    else if (mode === 'local') startLocal();
    else startBrowser();
  };

  const hoverTitle = locked ? lockMsg : (listening ? t('pm.voice.stop') : t('pm.voice.start'));

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={transcribing}
        title={hoverTitle}
        aria-label={hoverTitle}
        aria-pressed={listening}
        className={`absolute right-3 bottom-3 p-1 rounded-lg transition-colors ${listening
          ? 'text-red-500 animate-pulse'
          : locked
            ? (isDarkMode ? 'text-white/25 hover:text-white/40' : 'text-slate-300 hover:text-slate-400')
            : (isDarkMode ? 'text-white/40 hover:text-blue-200' : 'text-slate-400 hover:text-brand')
        }`}
      >
        <Mic size={20} />
      </button>
      {(listening || interim) && (
        <p className={`mt-1 text-sm ${isDarkMode ? 'text-blue-200' : 'text-brand'}`}>
          {t('pm.voice.listening')}{interim ? ` „${interim}…"` : '…'}
        </p>
      )}
      {transcribing && (
        <p className={`mt-1 text-sm ${isDarkMode ? 'text-blue-200' : 'text-brand'}`}>{t('pm.voice.transcribing')}</p>
      )}
      {note && (
        <p className={`mt-1 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          {note}
          <button onClick={() => setNote('')} className="ml-2 underline">{t('pm.common.done')}</button>
        </p>
      )}
    </>
  );
}

export default VoiceInput;
